import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDI } from "@/infrastructure/di/useDI";
import { asJobId } from "@/domain/model/Ids";
import type { JobReport, JobReportStatus, TravelWarningWrapper } from "@/domain/model/JobReport";
import { JobTimeline } from "./JobTimeline";
import { JobCrewList } from "./JobCrewList";
import {
  Stack,
  Box,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Button,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import MapIcon from "@mui/icons-material/Map";
import PrintIcon from "@mui/icons-material/Print";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import {
  PageContent,
  SectionCard,
  PageHeader,
  SummaryStatCard,
} from "@/adapters/inbound/layout";
import { Title } from "react-admin";

export function JobReportPage() {
  const { id } = useParams<{ id: string }>();
  const { jobReportService } = useDI();

  const [report, setReport] = useState<JobReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;

    const fetchReport = async () => {
      try {
        setIsLoading(true);
        setError(null);
        // Cast the raw URL string to your branded JobId
        const data = await jobReportService.getJobReport(asJobId(id));
        setReport(data);
      } catch (err: any) {
        if (err.name !== "AbortError") {
          setError(
            "Fehler beim Laden des Berichts. Bitte versuchen Sie es später erneut.",
          );
          console.error("Failed to fetch or map Job Report:", err);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();

    return;
  }, [id, jobReportService]);

  if (isLoading) {
    return (
      <PageContent width="content">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      </PageContent>
    );
  }

  if (error || !report) {
    return (
      <PageContent width="content">
        <Alert severity="error">{error || "Bericht nicht gefunden."}</Alert>
      </PageContent>
    );
  }

  // Helper for status colors
  const getStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "success";
      case "DAY_CLOSED":
        return "info";
      case "SUSPENDED":
        return "error";
      default:
        return "warning";
    }
  };

function statusLabel(status: JobReportStatus) {
  switch (status) {
    case "IN_PROGRESS":
      return "In Arbeit";
    case "SUSPENDED":
      return "Unterbrochen";

    case "DAY_CLOSED":
      return "Feierabend";

    case "COMPLETED":
      return "Abgeschlossen";

    default:
      return status;
  }
}

  // Convert seconds to HH:MM format for quick reading
  const formatHours = (seconds: number, hideZeroHours: boolean = false) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);

    if (hideZeroHours && hrs === 0) {
      return `${mins}m`;
    }
    return `${hrs}h ${mins.toString().padStart(2, "0")}m`;
  };

  const handlePrint = () => window.print();

  const renderWarningText = (w: TravelWarningWrapper) => {
    if (w.largeGap) return `Zeitlücke (${w.largeGap.minutes} Min.)`;
    if (w.veryLargeGaps) return `${w.veryLargeGaps.count} große Lücken`;
    if (w.sparseTracking) return "Lückenhafte Aufzeichnung";
    if (w.tooFewPoints) return "Zu wenig Datenpunkte";
    switch (w.type) {
      case "NO_SITE_GEOCODE":
        return "Keine Einsatzort-Koordinaten";
      case "NO_TRACKING_DATA":
        return "Keine Standortaufzeichnung";
      case "NO_CLEAR_TRIPS":
        return "Keine eindeutige Anfahrt erkannt";
      case "RESUME_AFTER_PAUSE_OUTSIDE_SITE":
        return "Nach Pause erneut außerhalb des Einsatzorts gestartet";
      default:
        return w.type;
    }
  };

  return (
    <PageContent width="content">
      <Title title="Auftragsbericht"/>
      <Stack spacing={3}>
        {/* Header */}
        <PageHeader
          title={report.context.title || "Einsatzbericht"}
          subtitle={`Kunde: ${report.context.customer.name}`}
          actions={
            <Stack
              spacing={1}
              alignItems={{ xs: "stretch", md: "flex-end" }}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <Chip
                label={statusLabel(report.context.status)}
                color={getStatusColor(report.context.status)}
                variant="outlined"
                sx={{ fontWeight: 600, height: 32 }}
              />

              <Button
                variant="contained"
                component={Link}
                to={`/dashboard/jobs/${report.context.jobId}/map`}
                startIcon={<MapIcon />}
                fullWidth
                sx={{ minWidth: { md: 200 } }}
              >
                Karte
              </Button>

              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                fullWidth
                sx={{ minWidth: { md: 200 } }}
              >
                Drucken
              </Button>
            </Stack>
          }
        />

        {/* 2. Context Card (UX Win: Added Period/Date) */}
        <SectionCard>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight="bold"
                display="block"
              >
                Zeitraum
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarMonthIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  {new Intl.DateTimeFormat("de-DE").format(
                    report.context.period.start,
                  )}{" "}
                  -{" "}
                  {new Intl.DateTimeFormat("de-DE").format(
                    report.context.period.end,
                  )}
                </Typography>
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight="bold"
                display="block"
              >
                Kunde
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {report.context.customer.name}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {report.context.customer.address?.street}{" "}
                {report.context.customer.address?.streetNumber},{" "}
                {report.context.customer.address?.zip}{" "}
                {report.context.customer.address?.city}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography
                variant="overline"
                color="text.secondary"
                fontWeight="bold"
                display="block"
              >
                Einsatzort
              </Typography>
              <Typography variant="body2">
                {report.context.site.label || "Baustelle"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {report.context.site.address?.street}{" "}
                {report.context.site.address?.streetNumber},{" "}
                {report.context.site.address?.zip}{" "}
                {report.context.site.address?.city}
              </Typography>
            </Grid>
          </Grid>
        </SectionCard>

        {/* 3. Stats Row (Unified Height) */}
        <Grid container spacing={3} mb={3}>
          {/* 1. Arbeitszeit */}
          <Grid size={{ xs: 12, md: 4 }}>
            <SummaryStatCard
              icon={<AccessTimeIcon />}
              label="Arbeitszeit (Netto)"
              value={formatHours(report.time.workSeconds)}
              subtext={`${formatHours(report.time.pauseSeconds, true)} Pause nicht eingerechnet`}
            />
          </Grid>

          {/* 2. Travel Estimate */}
          <Grid size={{ xs: 12, md: 4 }}>
            {/* Custom logic for Travel inside StatCard for height consistency */}
            <SummaryStatCard
              icon={<DirectionsCarIcon />}
              label="Fahrzeit (geschätzt)"
              value={formatHours(report.travelEstimate.totalTravelSeconds)}
              footer={
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  mt={0.5}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor:
                        report.travelEstimate.verdict === "USE"
                          ? "success.main"
                          : "warning.main",
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {report.travelEstimate.detectedTrips} Fahrten •{" "}
                    {Math.round(report.travelEstimate.confidence * 100)}%
                    Vertrauen
                  </Typography>

                  {report.travelEstimate.warnings.length > 0 ? (
                    <Tooltip
                      arrow
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              display: "block",
                              mb: 1,
                              borderBottom: "1px solid rgba(255,255,255,0.2)",
                              pb: 0.5,
                            }}
                          >
                            Qualitätshinweise:
                          </Typography>
                          <ul
                            style={{
                              margin: 0,
                              paddingLeft: "16px",
                              fontSize: "0.75rem",
                            }}
                          >
                            {report.travelEstimate.warnings.map((w, i) => (
                              <li key={i} style={{ marginBottom: "4px" }}>
                                {renderWarningText(w)}
                              </li>
                            ))}
                          </ul>
                        </Box>
                      }
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 16,
                          ml: 0.5,
                          color: "warning.main",
                          cursor: "help",
                        }}
                      />
                    </Tooltip>
                  ) : null}
                </Stack>
              }
            />
          </Grid>

          {/* 3. Team */}
          <Grid size={{ xs: 12, md: 4 }}>
            <SummaryStatCard
              icon={<CheckCircleIcon />}
              label="Teamstärke"
              value={`${report.crew.length} Mitarbeiter`}
              subtext="Personal vor Ort erfasst"
              accentColor="success.main"
            />
          </Grid>
        </Grid>

        {/* 4. Details Section (Timeline & Crew) */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <SectionCard title="Zeitverlauf">
              <JobTimeline events={report.timeline} />
            </SectionCard>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <SectionCard title="Stundenzettel">
              <JobCrewList crew={report.crew} />
            </SectionCard>
          </Grid>
        </Grid>
      </Stack>
    </PageContent>
  );
}
